// Tracker simples de tempo gasto por questão no Modo Estudo. Persiste em
// localStorage por banco (não vai pro backend). Espelha o que Prova faz com
// tempoPorQuestao, mas client-only — Estudo é exercício livre, não vale
// nota, então o tempo é só uma referência visual pro aluno.
//
// Modelo:
//   acumulado[qid]   = ms já gastos nessa questão em sessões anteriores
//   currentQid       = questão que está sendo cronometrada AGORA
//   currentEntrouEm  = timestamp ms de quando entrou na atual
//
// API:
//   startStudyTempo(bankId, qid)   inicia ou troca de questão (flusha a anterior)
//   flushStudyTempo()              acumula o elapsed da atual sem trocar
//   stopStudyTempo()               flusha e zera (saída do workspace)
//   getStudyTempoMs(qid)           ms totais (acumulado + elapsed se for a atual)
//   _tick                          signal de re-render (incrementa a cada 1s)
//
// Persistência: `newstudent:study:tempo:${bankId}` em localStorage. Sobreviver
// a refresh, sumir só se o aluno limpar storage manualmente.

import { signal } from "@preact/signals";

const STORAGE_PREFIX = "newstudent:study:tempo:";

interface StudyTempoState {
  bankId: string | null;
  acumulado: Record<string, number>;
  currentQid: string | null;
  currentEntrouEm: number; // ms (Date.now)
}

const state = signal<StudyTempoState>({
  bankId: null,
  acumulado: {},
  currentQid: null,
  currentEntrouEm: 0
});

// Tick exposto pra componentes assinarem e re-renderizarem a cada
// 1s o badge da questão ativa. Sem isso, a badge da ativa fica
// estática até navegar pra outra.
export const studyTempoTick = signal<number>(0);

function storageKey(bankId: string): string {
  return `${STORAGE_PREFIX}${bankId}`;
}

function loadFromStorage(bankId: string): Record<string, number> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(storageKey(bankId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, number> = {};
    for (const k of Object.keys(parsed)) {
      const v = (parsed as Record<string, unknown>)[k];
      if (typeof v === "number" && v >= 0) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function persistAcumulado(bankId: string, acumulado: Record<string, number>) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(storageKey(bankId), JSON.stringify(acumulado));
  } catch {
    // quota / private mode
  }
}

/** Inicia o tracker pra um banco específico. Carrega o acumulado do storage. */
export function initStudyTempo(bankId: string) {
  if (state.value.bankId === bankId) return; // já inicializado
  state.value = {
    bankId,
    acumulado: loadFromStorage(bankId),
    currentQid: null,
    currentEntrouEm: 0
  };
}

/** Troca a questão sendo cronometrada. Flusha a anterior pro acumulado. */
export function startStudyTempo(bankId: string, qid: string) {
  // Garante que tracker está no banco certo.
  if (state.value.bankId !== bankId) {
    initStudyTempo(bankId);
  }
  flushStudyTempo();
  const now = Date.now();
  state.value = {
    ...state.value,
    currentQid: qid,
    currentEntrouEm: now
  };
}

/** Acumula o elapsed da atual em acumulado, sem trocar de questão. Útil ao
 *  pausar, mudar tab, etc. Idempotente — chamar duas vezes seguidas é seguro. */
export function flushStudyTempo() {
  const s = state.value;
  if (!s.bankId || !s.currentQid || !s.currentEntrouEm) return;
  const now = Date.now();
  const elapsed = Math.max(0, now - s.currentEntrouEm);
  if (elapsed <= 0) return;
  const acumulado = {
    ...s.acumulado,
    [s.currentQid]: (s.acumulado[s.currentQid] ?? 0) + elapsed
  };
  state.value = {
    ...s,
    acumulado,
    currentEntrouEm: now
  };
  persistAcumulado(s.bankId, acumulado);
}

/** Para de cronometrar (saída do workspace). Flusha e zera atual. */
export function stopStudyTempo() {
  flushStudyTempo();
  const s = state.value;
  state.value = {
    ...s,
    currentQid: null,
    currentEntrouEm: 0
  };
}

/** Tempo total (ms) gasto numa questão. Pra atual, soma o elapsed live. */
export function getStudyTempoMs(qid: string): number {
  const s = state.value;
  // Subscrição implícita ao tick — força recomputo pra questão ativa.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  studyTempoTick.value;
  const acumulado = s.acumulado[qid] ?? 0;
  if (s.currentQid === qid && s.currentEntrouEm > 0) {
    return acumulado + Math.max(0, Date.now() - s.currentEntrouEm);
  }
  return acumulado;
}

/** Soma total de ms gastos no banco atual. Soma todo o acumulado + elapsed
 *  da questão ativa. Útil pro cronômetro "Total" no header. */
export function getStudyTempoTotalMs(): number {
  const s = state.value;
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  studyTempoTick.value;
  let total = 0;
  for (const k of Object.keys(s.acumulado)) {
    total += s.acumulado[k] ?? 0;
  }
  if (s.currentQid && s.currentEntrouEm > 0) {
    total += Math.max(0, Date.now() - s.currentEntrouEm);
  }
  return total;
}

/** Id da questão atualmente sendo cronometrada (ou null se nenhuma). */
export function getStudyCurrentQid(): string | null {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  studyTempoTick.value;
  return state.value.currentQid;
}

/** Zera o tempo cronometrado de UMA chave (questão ou item). Se a chave
 *  for a atual em currentQid, também reinicia o currentEntrouEm pra não
 *  acumular o que foi gasto antes. Usado pelo "Recomeçar questão". */
export function clearStudyTempoForKeys(keys: string[]) {
  const s = state.value;
  if (!s.bankId) return;
  const acumulado = { ...s.acumulado };
  for (const k of keys) delete acumulado[k];
  const currentEntrouEm =
    s.currentQid && keys.includes(s.currentQid) ? Date.now() : s.currentEntrouEm;
  state.value = { ...s, acumulado, currentEntrouEm };
  persistAcumulado(s.bankId, acumulado);
}

/** Lê o tempo total acumulado dum banco direto do localStorage, sem precisar
 *  que o tracker tenha sido inicializado. Útil pro BankDetailView mostrar
 *  "tempo investido" pro banco que o aluno NÃO está estudando agora.
 *  Retorna 0 se sem dados. */
export function readBankStudyTimeMs(bankId: string): number {
  const acumulado = loadFromStorage(bankId);
  let total = 0;
  for (const k of Object.keys(acumulado)) total += acumulado[k] ?? 0;
  return total;
}

/** Zera TODOS os tempos do banco atual. Reinicia o cronômetro da atual em 0
 *  (caso o aluno permaneça na mesma questão após recomeçar). */
export function clearStudyTempoAll() {
  const s = state.value;
  if (!s.bankId) return;
  state.value = {
    ...s,
    acumulado: {},
    currentEntrouEm: s.currentQid ? Date.now() : 0
  };
  persistAcumulado(s.bankId, {});
}

// =============================================================================
// Tick e visibility — controlado pelo StudyWorkspace ao montar/desmontar.
// =============================================================================

let tickInterval: ReturnType<typeof setInterval> | null = null;

export function startStudyTempoTicking() {
  if (tickInterval) return;
  if (typeof window === "undefined") return;
  tickInterval = setInterval(() => {
    studyTempoTick.value = studyTempoTick.value + 1;
  }, 1000);
  // Quando o aluno troca de tab/minimiza, pausa cronometragem da atual
  // (flusha) e re-inicia ao voltar. Sem isso, sair pra responder email
  // e voltar conta 20min nessa questão.
  document.addEventListener("visibilitychange", onVisibilityChange);
}

export function stopStudyTempoTicking() {
  if (tickInterval) {
    clearInterval(tickInterval);
    tickInterval = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
}

function onVisibilityChange() {
  if (document.hidden) {
    flushStudyTempo();
    // Marca como "pausado" zerando entrouEm — getStudyTempoMs trata como
    // se não estivesse cronometrando.
    state.value = { ...state.value, currentEntrouEm: 0 };
  } else if (state.value.currentQid) {
    // Voltou — retoma cronometragem da mesma questão.
    state.value = { ...state.value, currentEntrouEm: Date.now() };
  }
}
